import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Check, Loader2, Infinity as InfinityIcon, Gift, RefreshCw, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const fmtGb = (mb) => (mb >= 1024 ? `${Math.round(mb / 1024)} GB` : `${mb} MB`);
const fmtPrice = (p) => (Number.isInteger(p) ? `${p}` : p.toFixed(2).replace(".", ","));

const Piani = () => {
  const [data, setData] = useState(null);
  const [storage, setStorage] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [mode, setMode] = useState("sub"); // "sub" | "onetime"
  const [interval, setIntervalState] = useState("year"); // "month" | "year"

  useEffect(() => {
    api.get("/plans").then((r) => setData(r.data)).catch(() => {});
    api.get("/storage").then((r) => setStorage(r.data)).catch(() => {});
  }, []);

  const buy = async (lookup_key) => {
    if (!lookup_key) return;
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
      setLoadingKey(null);
    }
  };

  const subs = data?.subscription || [];
  const onetime = data?.onetime || [];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-10 fade-in">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Custodisci i ricordi, per sempre</h1>
          <p className="text-muted-foreground mt-3">Scegli come custodire la tua storia: un piccolo canone o un pagamento unico. Cambi o disdici quando vuoi.</p>
        </div>
        {storage && (
          <p className="text-center text-sm text-muted-foreground mb-8" data-testid="piani-current">
            Piano attuale: <span className="font-medium text-[#2C2A29]">{storage.plan_name}</span> · {fmtGb(storage.limit_mb)}
          </p>
        )}

        {/* Piano Free */}
        <div className="max-w-3xl mx-auto mb-10 rounded-3xl border-2 border-[#81B29A] bg-gradient-to-br from-[#EEF3EC] to-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="plan-free">
          <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shrink-0 border border-[#cfe0d3]">
            <Gift className="w-7 h-7 text-[#4b8f6f]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif text-2xl">Piano Free</h3>
              <span className="text-xs bg-[#81B29A] text-white px-3 py-1 rounded-full">Gratis per sempre</span>
            </div>
            <p className="mt-1 text-[#3d3a38]">
              Inizia subito con <span className="font-serif text-3xl text-[#2C2A29] align-middle">300&nbsp;MB</span> di spazio gratuito, senza carta di credito.
            </p>
            <p className="text-sm text-muted-foreground mt-1">Perfetto per scrivere i primi ricordi e provare Eterna.</p>
          </div>
          {storage?.plan_name === "Free" && (
            <span className="shrink-0 inline-flex items-center gap-1.5 text-sm text-[#4b8f6f] font-medium"><Check className="w-4 h-4" /> Piano attivo</span>
          )}
        </div>

        {/* Toggle Canone / Una tantum */}
        <div className="flex justify-center mb-3">
          <div className="inline-flex bg-[#F1ECE6] rounded-full p-1" data-testid="mode-toggle">
            <button onClick={() => setMode("sub")}
              className={`px-5 sm:px-7 py-2 rounded-full text-sm font-medium transition-colors ${mode === "sub" ? "bg-white shadow text-[#2C2A29]" : "text-muted-foreground"}`}
              data-testid="mode-sub">A canone</button>
            <button onClick={() => setMode("onetime")}
              className={`px-5 sm:px-7 py-2 rounded-full text-sm font-medium transition-colors ${mode === "onetime" ? "bg-white shadow text-[#2C2A29]" : "text-muted-foreground"}`}
              data-testid="mode-onetime">Una tantum</button>
          </div>
        </div>

        {mode === "sub" ? (
          <>
            <div className="flex justify-center items-center gap-3 mb-8">
              <div className="inline-flex bg-[#F1ECE6] rounded-full p-1" data-testid="interval-toggle">
                <button onClick={() => setIntervalState("month")}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${interval === "month" ? "bg-white shadow text-[#2C2A29]" : "text-muted-foreground"}`}
                  data-testid="interval-month">Mensile</button>
                <button onClick={() => setIntervalState("year")}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${interval === "year" ? "bg-white shadow text-[#2C2A29]" : "text-muted-foreground"}`}
                  data-testid="interval-year">Annuale</button>
              </div>
              <span className="text-xs bg-[#EAF2EC] text-[#4b8f6f] px-3 py-1 rounded-full font-medium">Risparmia ~2 mesi</span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {subs.map((p, i) => {
                const opt = p[interval];
                return (
                  <div key={p.name} className={`bg-white rounded-2xl border p-7 flex flex-col ${i === 1 ? "border-primary shadow-md" : "border-border"}`} data-testid={`sub-${p.name}`}>
                    {i === 1 && <span className="self-start text-xs bg-[#FBEDE7] text-primary px-3 py-1 rounded-full mb-3">Più scelto</span>}
                    <h3 className="font-serif text-2xl">{p.name}</h3>
                    <div className="mt-3 flex items-end gap-1.5">
                      <span className="text-4xl font-serif">€{fmtPrice(opt.price)}</span>
                      <span className="text-muted-foreground mb-1.5">/{interval === "month" ? "mese" : "anno"}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Rinnovo automatico · disdici quando vuoi</p>
                    <ul className="mt-5 space-y-2 text-sm flex-1">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#81B29A]" /> {fmtGb(p.storage_mb)} di spazio</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#81B29A]" /> Tutori ed eredi</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#81B29A]" /> Testamento e Capsula del Tempo</li>
                    </ul>
                    <Button onClick={() => buy(opt.lookup_key)} disabled={loadingKey === opt.lookup_key}
                      className={`mt-6 rounded-full h-11 ${i === 1 ? "bg-primary hover:bg-[#c9694f] text-white" : "bg-[#2C2A29] hover:bg-black text-white"}`}
                      data-testid={`buy-${opt.lookup_key}`}>
                      {loadingKey === opt.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : "Abbonati"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {onetime.map((p, i) => (
              <div key={p.lookup_key} className={`bg-white rounded-2xl border p-6 flex flex-col ${p.lifetime ? "border-primary shadow-md" : "border-border"}`} data-testid={`plan-${p.lookup_key}`}>
                {p.lifetime && <span className="self-start text-xs bg-[#FBEDE7] text-primary px-3 py-1 rounded-full mb-3 inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> A vita</span>}
                <h3 className="font-serif text-2xl">{p.name}</h3>
                <div className="mt-3 flex items-end gap-1.5">
                  <span className="text-4xl font-serif">€{fmtPrice(p.price)}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">una tantum · {p.lifetime ? "per sempre" : `${p.years} anni`}</p>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#81B29A]" /> {fmtGb(p.storage_mb)} di spazio</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#81B29A]" /> Tutori ed eredi</li>
                  <li className="flex items-center gap-2"><InfinityIcon className="w-4 h-4 text-[#81B29A]" /> {p.lifetime ? "Conservazione a vita" : `Conservazione ${p.years} anni`}</li>
                </ul>
                <Button onClick={() => buy(p.lookup_key)} disabled={loadingKey === p.lookup_key}
                  className={`mt-6 rounded-full h-11 ${p.lifetime ? "bg-primary hover:bg-[#c9694f] text-white" : "bg-[#2C2A29] hover:bg-black text-white"}`}
                  data-testid={`buy-${p.lookup_key}`}>
                  {loadingKey === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : "Scegli piano"}
                </Button>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground mt-10">Pagamento sicuro con Stripe · carta di test 4242 4242 4242 4242</p>
      </main>
    </div>
  );
};

export default Piani;
